#version 300 es
precision highp float;
precision highp int;

// Hero atmosphere. Cloud waves carry the composition in both themes.
// Light: warm dawn sky with a golden sun.
// Dark:  cosmic night sky with a pale moon and nebula clouds.

#include "_lib.glsl"

in vec2 v_uv;
out vec4 fragColor;

uniform float u_time;
uniform vec2  u_resolution;
uniform vec2  u_mouse;           // smoothed -1..1
uniform float u_scrollVelocity;  // 0..1 decaying
uniform float u_dark;            // 0 light, 1 dark

void main() {
  vec2 uv = v_uv;
  float aspect = u_resolution.x / max(u_resolution.y, 1.0);
  vec2 p = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);

  // ---- Sun / moon position ---------------------------------------------
  vec2 sunPos = vec2(u_mouse.x * 0.45 * aspect, 0.22 - u_mouse.y * 0.18);
  float sunDist = length(p - sunPos);

  // ---- Two complete palette sets ---------------------------------------
  // LIGHT: warm dawn.
  vec3 lSkyTop   = vec3(0.86, 0.86, 0.94);
  vec3 lSkyMid   = vec3(0.99, 0.95, 0.88);
  vec3 lSkyHorz  = vec3(1.00, 0.92, 0.80);
  vec3 lCloudLo  = vec3(0.93, 0.93, 0.97);
  vec3 lCloudHi  = vec3(1.00, 0.92, 0.78);
  vec3 lSun      = vec3(1.00, 0.85, 0.55);
  vec3 lHalo     = vec3(1.00, 0.78, 0.45);
  vec3 lHorizon  = vec3(1.00, 0.94, 0.84);

  // DARK: cosmic night. Deep indigo base, luminous nebula clouds.
  // Sky darkened to boost cloud contrast; cloud highs pushed brighter
  // with a cyan undertone so the waves glow against the night sky.
  vec3 dSkyTop   = vec3(0.03, 0.04, 0.10);   // deep space
  vec3 dSkyMid   = vec3(0.06, 0.07, 0.16);
  vec3 dSkyHorz  = vec3(0.10, 0.10, 0.22);
  vec3 dCloudLo  = vec3(0.14, 0.18, 0.34);   // shadowed nebula (slight violet)
  vec3 dCloudHi  = vec3(0.82, 0.90, 1.00);   // bright cyan-lit crest
  vec3 dSun      = vec3(0.98, 0.98, 1.00);
  vec3 dHalo     = vec3(0.60, 0.72, 1.00);   // more cyan in the moon halo
  vec3 dHorizon  = vec3(0.08, 0.07, 0.18);

  // Lerp whole palette by u_dark.
  vec3 skyTop   = mix(lSkyTop,   dSkyTop,   u_dark);
  vec3 skyMid   = mix(lSkyMid,   dSkyMid,   u_dark);
  vec3 skyHorz  = mix(lSkyHorz,  dSkyHorz,  u_dark);
  vec3 cloudLo  = mix(lCloudLo,  dCloudLo,  u_dark);
  vec3 cloudHi  = mix(lCloudHi,  dCloudHi,  u_dark);
  vec3 sunColor = mix(lSun,      dSun,      u_dark);
  vec3 haloCol  = mix(lHalo,     dHalo,     u_dark);
  vec3 horizCol = mix(lHorizon,  dHorizon,  u_dark);

  // ---- Sky depth --------------------------------------------------------
  float skyT = smoothstep(0.0, 1.0, uv.y);
  vec3 sky = palette5(skyT, skyTop, skyMid, skyMid, skyHorz, skyHorz);

  // ---- Sun / moon disc + halo ------------------------------------------
  float discR = mix(0.14, 0.19, u_dark);
  float haloR = mix(0.55, 0.80, u_dark);
  float disc  = smoothstep(discR, 0.0, sunDist);
  float halo  = smoothstep(haloR, 0.0, sunDist) * mix(0.55, 0.75, u_dark);

  // ---- Cloud waves ------------------------------------------------------
  // Dark mode gets more flow — time-scale bumped + second warp layer for
  // a fluid, living nebula. Mask widened so more of the scene is clouds
  // at night, and contrast softened so the soft edges aren't clipped.
  float scrollBoost = u_scrollVelocity * 0.6;
  float driftX = mix(0.03, 0.06, u_dark);
  float driftY = mix(-0.012, -0.022, u_dark);
  float warpRate = mix(0.4, 0.65, u_dark);
  vec2 cp = p * mix(1.6, 1.3, u_dark) + vec2(u_time * driftX, u_time * driftY);
  float clouds = fbmWarp(cp, u_time * warpRate);
  // Softer mask edges in dark mode = more graceful wave falloff.
  float maskLo = mix(0.40, 0.28, u_dark);
  float maskHi = mix(0.78, 0.72, u_dark);
  float cloudMask = smoothstep(maskLo, maskHi, clouds + scrollBoost * 0.18);
  float cloudShadow = 1.0 - mix(0.45, 0.55, u_dark) * cloudMask;
  // Moonlight reaches further through the night clouds than sun through
  // dawn clouds — wider radius.
  float cloudLit = smoothstep(mix(0.55, 0.85, u_dark), 0.0, sunDist);
  vec3 cloudColor = mix(cloudLo, cloudHi, cloudLit);

  // ---- Compose ---------------------------------------------------------
  vec3 col = sky * cloudShadow;
  col = mix(col, cloudColor, cloudMask * mix(0.70, 0.92, u_dark));
  col += sunColor * disc;
  col += haloCol * halo * (0.55 + 0.35 * (1.0 - cloudMask));

  // Dark mode: add a subtle secondary bright-crest pass that picks out
  // the tops of the cloud waves — gives them that luminous nebula feel
  // without cranking the main cloud highlight to blown-out white.
  float crest = smoothstep(0.62, 0.88, clouds);
  col += crest * u_dark * 0.18 * vec3(0.85, 0.95, 1.00);

  // Horizon haze along the bottom edge.
  float horizon = smoothstep(0.0, 0.35, uv.y);
  col = mix(horizCol, col, horizon);

  // Film grain.
  float grain = (hash(gl_FragCoord.xy + u_time * 13.0) - 0.5);
  col += grain * mix(0.012, 0.008, u_dark);

  fragColor = vec4(col, 1.0);
}
